import NoResultIcon from '@/common/assets/icons/NoResultIcon.svg?react';
import LoadingButton from '@/common/components/base/button/loadingButton';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import RadioButton from '@/common/components/base/input/radioButton/RadioButton';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { ONLY_NUMBERS_WITH_SEPARATORS_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import MainModal from '@/common/components/base/modal/Modal';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import { useSampleWages } from '@/common/hooks/useSampleWages';
import { SelectionOptionsType } from '@/common/types/SelectionOptions.type';
import { requiredText } from '@/common/utils/rules/publicRules';
import { WagesModalType } from '@/modules/adjusterReportModules/IncidentParticipants/components/modals/WagesModal.type';
import { WagesTypeForm } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getWagesList/getWagesListQuery.type';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { getBodyVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyVehicleInformation/getBodyVehicleInformationOption';
import { patchAddBodyWegasQueryOption } from '@/modules/adjusterReportModules/Insurer-information/queries/patchAddBodyWegas/patchAddBodyWegasQueryOption';
import { patchBodyWagesQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyWages/patchBodyWagesQueryOptions';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const BodyWagesModal: React.FC<WagesModalType> = ({
  open,
  setOpenModal,
  onComplete,
  formData,
}) => {
  const { id } = useParams();
  const { data: vehicleInformationData } = useQuery(
    getBodyVehicleInformationOption({
      id: +id!,
    }),
  );
  const [name, setName] = useState('');
  const { sampleWagesList } = useSampleWages({
    name: name,
    vehicleType: vehicleInformationData?.vehicle_type?.name,
  });
  const methods = useForm<WagesTypeForm>({
    mode: 'onChange',
  });
  const priceWatcher = useWatch({ control: methods?.control, name: 'price' });
  const { mutate: addWegasQuery, isPending: addwagesPending } = useMutation({
    ...patchAddBodyWegasQueryOption(),
    onSuccess: () => {
      onComplete && onComplete();
      setOpenModal(false);
      methods.reset();
    },
  });
  const { mutate: patchWagesQuery, isPending: patchPending } = useMutation({
    ...patchBodyWagesQueryOptions(),
    onSuccess: () => {
      onComplete && onComplete();
      setOpenModal(false);
      methods.reset();
    },
  });
  const patchWages = (data: WagesTypeForm) => {
    patchWagesQuery(
      {
        data: {
          ...data,
          title:
            data?.title?.value === 'others'
              ? data?.titleOfOtherOption
              : data?.title?.label,
          price: Number((data?.price ?? '0').toString().replace(/,/g, '')),
        },
        id: +id!,
        wages_id: data?.id!,
        source: 'pwa',
      },
      {
        onSuccess: () => {
          onComplete && onComplete();
          setOpenModal(false);
          methods.reset();
        },
      },
    );
  };

  const addWages = (data: WagesTypeForm) => {
    addWegasQuery(
      {
        data: {
          id: data?.id,
          title:
            data?.title?.value === 'others'
              ? (data?.titleOfOtherOption ?? '')
              : (data?.title?.label ?? ''),
          price: Number((data?.price ?? '0').toString().replace(/,/g, '')),
        },
        id: +id!,
      },
      {
        onSuccess: () => {
          onComplete && onComplete();
          setOpenModal(false);
          methods.reset();
        },
      },
    );
  };

  const title = methods.watch('title');

  const onSubmit = (data: WagesTypeForm) => {
    formData?.title?.label ? patchWages(data) : addWages(data);
  };

  useEffect(() => {
    if (formData) {
      const isCurrentWageInTheSampleWagesList = sampleWagesList?.find(
        (item) => item?.label === formData?.title?.label,
      );
      isCurrentWageInTheSampleWagesList
        ? methods.reset({
            ...formData,
            price: formatNumberWithCommas(formData?.price),
          })
        : methods.reset({
            title: { label: 'سایر', value: 'others' },
            titleOfOtherOption: formData?.title?.label,
            price: formatNumberWithCommas(formData?.price),
            id: formData?.id,
          });
    }
  }, [formData]);

  useEffect(() => {
    if (priceWatcher)
      methods.reset({
        ...formData,
        title: {
          label: methods?.getValues('title.label'),
          value: methods?.getValues('title.value'),
        },
        price: formatNumberWithCommas(methods?.getValues('price') ?? '0'),
        titleOfOtherOption: methods?.getValues('titleOfOtherOption'),
      });
  }, [priceWatcher]);

  useEffect(() => {
    if (!open)
      methods.reset({
        ...formData,
        price: formatNumberWithCommas(formData?.price ?? ''),
      });
  }, [open]);

  return (
    <MainModal open={open} closeModal={() => setOpenModal(false)}>
      <TitleDescription>
        {formData ? 'ویرایش' : 'افزودن نوع تعمیر'}
      </TitleDescription>
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={8} mt={24}>
          <Box>
            <ModalInput
              name="title"
              label="عنوان تعمیر"
              title="عنوان تعمیر"
              placeholder="انتخاب عنوان تعمیر"
              options={sampleWagesList ?? []}
              searchOptions={{
                onChange: (e) => {
                  setName(e);
                },
                placeholder: 'جستجو کنید',
              }}
              rules={{ required: requiredText }}
            >
              {(closeModal) => (
                <>
                  <RadioOptionModal
                    options={sampleWagesList}
                    onChange={(selectedOption: SelectionOptionsType) => {
                      methods?.setValue('title', selectedOption);
                    }}
                  />
                  <LoadingButton
                    mt={32}
                    fullWidth
                    onClick={() => closeModal()}
                    disabled={!methods?.getValues('title.label')}
                    loading={patchPending || addwagesPending}
                  >
                    ثبت
                  </LoadingButton>
                </>
              )}
            </ModalInput>
          </Box>
          <Box>
            {title?.value === 'others' && (
              <TextInput
                label="نوع تعمیر(سایر)"
                name="titleOfOtherOption"
                placeholder="نام نوع تعمیر را وارد کنید"
                rules={{ required: requiredText }}
              />
            )}
          </Box>
          <Box>
            <NumberInputWithWords
              label="قیمت"
              name="price"
              placeholder="قیمت را وارد کنید"
              rules={{
                required: requiredText,
                ...ONLY_NUMBERS_WITH_SEPARATORS_RULE,
              }}
            />
          </Box>
          <Box w="100%">
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={patchPending || addwagesPending}
              fullWidth
              disabled={!methods?.formState?.isValid}
            >
              ثبت و ادامه
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
    </MainModal>
  );
};
export default BodyWagesModal;

const RadioOptionModal = ({
  options,
  onChange,
}: {
  options?: SelectionOptionsType[];
  onChange: (option: SelectionOptionsType) => void;
}) => {
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);
  return (
    <Box className={css({ maxHeight: '40vh', overflowY: 'auto' })}>
      {options?.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt="50px"
        >
          <NoResultIcon />
          <Typography
            textStyle="b2"
            textAlign="center"
            py="13px"
            color="{colors.black-3}"
          >
            نتیجه‌ای یافت نشد !
          </Typography>
        </Box>
      ) : (
        <Box>
          {options?.map((option) => (
            <Box
              key={option.value}
              py="13px"
              borderBottom="1px solid #f0f0f0"
              cursor="pointer"
            >
              <RadioButton
                name="title.label"
                onChange={() => onChange(option)}
                value={option?.label}
                label={option?.label}
              />
            </Box>
          ))}
          <Box
            key={'others'}
            py="13px"
            borderBottom="1px solid #f0f0f0"
            cursor="pointer"
          >
            <RadioButton
              name="title.label"
              onChange={() => onChange({ label: 'سایر', value: 'others' })}
              value={'others'}
              label={'سایر (اضافه کردن نوع تعمیر دلخواه) '}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
